import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  FileText,
  Calculator,
  Monitor,
  BookOpen,
  Lightbulb,
  Shield,
  Globe,
  GraduationCap,
  CheckCircle2,
  ArrowRight,
  ChevronDown,
} from "lucide-react";
import { JSX, useState } from "react";
import { services } from "@/data";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/services/$slug")({
  loader: ({ params }) => {
    const service = services.find((s) => s.slug === params.slug);
    if (!service) throw notFound();
    return { service };
  },
  component: ServiceDetailPage,
});

const iconMap: Record<string, React.ElementType> = {
  FileText,
  Calculator,
  Monitor,
  BookOpen,
  Lightbulb,
  Shield,
  Globe,
  GraduationCap,
};

// eslint-disable-next-line react-refresh/only-export-components
function ServiceDetailPage(): JSX.Element {
  const { service } = Route.useLoaderData();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const related = services.filter((s) =>
    service.relatedServices.includes(s.slug),
  );

  return (
    <>
      <Helmet>
        <title>{service.metaTitle}</title>
        <meta name="description" content={service.metaDescription} />
        <link
          rel="canonical"
          href={`https://rosewoodtax.co.ke/services/${service.slug}`}
        />
      </Helmet>

      <section className="bg-[#FAF7F2] pt-16 pb-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[#6B6B6B] mb-8">
            <Link to="/" className="hover:text-[#6B1E2A] transition-colors">
              Home
            </Link>
            <span>/</span>
            <Link
              to="/services"
              className="hover:text-[#6B1E2A] transition-colors"
            >
              Services
            </Link>
            <span>/</span>
            <span className="text-[#1A1A1A] font-medium">{service.title}</span>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="font-serif text-5xl font-semibold text-[#1A1A1A] mb-5 leading-tight">
                {service.title}
              </h1>
              <p className="text-xl text-[#6B6B6B] leading-relaxed mb-8">
                {service.shortDescription}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#6B1E2A] text-white font-semibold rounded-xl hover:bg-[#4A1219] transition-colors"
                >
                  Get Started
                  <ArrowRight size={15} />
                </Link>
                <a
                  href="tel:+254703173405"
                  className="inline-flex items-center gap-2 px-6 py-3  text-[#6B1E2A] font-semibold rounded-xl transition-colors"
                >
                  Call Us
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-white rounded-2xl p-7"
            >
              <h3 className="font-semibold text-[#1A1A1A] mb-5 flex items-center gap-2">
                <span className="w-1 h-5 bg-[#6B1E2A] rounded-full" />
                What's Included
              </h3>
              <ul className="space-y-3">
                {service.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <CheckCircle2
                      size={16}
                      className="text-[#6B1E2A] mt-0.5 flex-shrink-0"
                    />
                    <span className="text-sm text-[#1A1A1A]">{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Full description */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-14">
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="font-serif text-3xl font-semibold text-[#1A1A1A] mb-6">
                  About This Service
                </h2>
                <div className="prose prose-lg text-[#6B6B6B] leading-relaxed space-y-4">
                  {service.fullDescription.split("\n\n").map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </motion.div>

              {/* Process steps */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="mt-14"
              >
                <h2 className="font-serif text-3xl font-semibold text-[#1A1A1A] mb-8">
                  Our Process
                </h2>
                <div className="grid sm:grid-cols-2 gap-5">
                  {service.process.map((step, i) => (
                    <motion.div
                      key={step.step}
                      initial={{ opacity: 0, y: 15 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.08 }}
                      className="flex gap-4 p-5 bg-[#FAF7F2] rounded-xl"
                    >
                      <div className="w-9 h-9 bg-[#6B1E2A] rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-[#C9A84C] font-bold text-sm">
                          {step.step}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#1A1A1A] mb-1">
                          {step.title}
                        </h4>
                        <p className="text-sm text-[#6B6B6B] leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* FAQs */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-14"
              >
                <h2 className="font-serif text-3xl font-semibold text-[#1A1A1A] mb-8">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-3">
                  {service.faqs.map((faq, i) => (
                    <div key={i} className="rounded-xl overflow-hidden">
                      <button
                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                        className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-[#FAF7F2] transition-colors"
                      >
                        <span className="font-medium text-[#1A1A1A]">
                          {faq.question}
                        </span>
                        <ChevronDown
                          size={18}
                          className={cn(
                            "text-[#6B6B6B] flex-shrink-0 transition-transform duration-200",
                            openFaq === i && "rotate-180",
                          )}
                        />
                      </button>
                      {openFaq === i && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="px-5 pb-5"
                        >
                          <p className="text-[#6B6B6B] leading-relaxed text-sm">
                            {faq.answer}
                          </p>
                        </motion.div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            <div className="space-y-6">
              <div className="bg-[#6B1E2A] rounded-2xl p-7 text-white">
                <h3 className="font-serif text-2xl font-semibold mb-2">
                  Ready to Get Started?
                </h3>
                <p className="text-white/70 text-sm mb-6 leading-relaxed">
                  Book a free consultation with our {service.title} specialists
                  today.
                </p>
                <Link
                  to="/contact"
                  className="block text-center px-5 py-3 bg-[#C9A84C] text-[#1A1A1A] font-semibold rounded-xl hover:bg-[#E2C06B] transition-colors text-sm mb-3"
                >
                  Book Free Consultation
                </Link>
                <a
                  href="tel:+254703173405"
                  className="block text-center px-5 py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors text-sm"
                >
                  +254 703 173405
                </a>

                <div className="mt-6 pt-5 space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-[#C9A84C]" />
                    <span className="text-white/70 text-xs">
                      Free initial consultation
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-[#C9A84C]" />
                    <span className="text-white/70 text-xs">
                      ICPAK certified experts
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-[#C9A84C]" />
                    <span className="text-white/70 text-xs">
                      100% confidential
                    </span>
                  </div>
                </div>
              </div>

              {related.length > 0 && (
                <div className="bg-[#FAF7F2] rounded-2xl p-6">
                  <h4 className="font-semibold text-[#1A1A1A] mb-4 text-sm uppercase tracking-wider">
                    Related Services
                  </h4>
                  <div className="space-y-2">
                    {related.map((s) => {
                      const RelatedIcon = iconMap[s.icon] || FileText;
                      return (
                        <Link
                          key={s.id}
                          to={`/services/${s.slug}` as never}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-white transition-colors group"
                        >
                          <div className="w-8 h-8 bg-[#6B1E2A]/10 rounded-lg flex items-center justify-center">
                            <RelatedIcon size={15} className="text-[#6B1E2A]" />
                          </div>
                          <span className="text-sm font-medium text-[#1A1A1A] group-hover:text-[#6B1E2A] transition-colors">
                            {s.title}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
